import React, { useEffect, useState } from 'react';
import './mcp.css';
import { useNavigate } from 'react-router-dom';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

const STATUS_STYLES = {
  active: 'border-sage/40 text-sage',
  paused: 'border-sun-400/65 text-sun-600',
  paused_error: 'border-ember/40 text-ember',
  draft: 'border-ink-200/40 text-ink-200',
  completed: 'border-ink-200/40 text-ink-200',
};

const STATUS_LABELS = {
  active: 'Active',
  paused: 'Paused',
  paused_error: 'Needs attention',
  draft: 'Draft',
  completed: 'Completed',
};

const CYCLE_MODEL_LABELS = {
  rolling_replace: 'Rolling replace',
  stacking: 'Stacking flights',
  refresh_in_place: 'Refresh in place',
};

const TRIGGER_LABELS = {
  schedule: 'Recurring schedule',
  route_match: 'Route match',
  stop_frequency: 'Stop frequency',
  zone_entry: 'Zone entry',
};

function formatLaunchTime(launchTime) {
  if (!launchTime) return '—';
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(launchTime);
  if (!match) return launchTime;
  const hour24 = Number(match[1]);
  const minute = match[2];
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute} ${period}`;
}

function cadenceSummary(a) {
  const cadenceLabel =
    a.cadence === 'daily' ? 'Daily'
      : a.cadence === 'weekly' ? 'Weekly'
        : a.cadence === 'monthly' ? 'Monthly'
          : `Every ${a.intervalDays ?? '?'} days`;
  return `${cadenceLabel} · ${formatLaunchTime(a.launchTime)} · ${a.timezone || '—'}`;
}

const McpAutomations = () => {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/automations', { headers: authHeader() })
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => {
        const list = Array.isArray(data) ? data : data.automations || data.data || [];
        setAutomations(list);
        setLoading(false);
      })
      .catch(() => {
        setAutomations([]);
        setError('Could not load automations.');
        setLoading(false);
      });
  }, []);

  return (
    <div className="mcp-page" style={{ padding: 0 }}>
      <McpHeader title="Automations" />

      <div className="mx-auto w-full max-w-7xl p-6 md:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold text-ink">Campaign automations</h2>
            <p className="mt-1 text-sm text-ink-200">
              Recurring campaigns that launch on a schedule from your fleet data.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/client/mcp/automations/new')}
            className="inline-flex items-center gap-1.5 rounded-full bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun transition hover:bg-sun-400 active:scale-[0.98]"
          >
            + New automation
          </button>
        </div>

        {loading && (
          <div className="mt-12 text-center text-sm text-ink-200">Loading automations…</div>
        )}

        {error && !loading && (
          <div className="mt-12 rounded-3xl border-2 border-dashed border-ember/40 bg-ember/5 p-12 text-center">
            <h3 className="font-display text-xl font-bold text-ink">Couldn't load automations</h3>
            <p className="mt-2 text-sm text-ink-100">{error}</p>
          </div>
        )}

        {!loading && !error && automations.length === 0 && (
          <div className="mt-12 rounded-3xl border-2 border-dashed border-paper-300 bg-paper-50 p-12 text-center">
            <div className="mx-auto h-12 w-12 -rotate-45 rounded-[50%_50%_50%_0] shadow-[0_12px_28px_-8px_rgba(31,79,229,0.55)]"
              style={{ background: 'linear-gradient(120deg,#1F4FE5,#29C5FF)' }} />
            <h3 className="mt-6 font-display text-xl font-bold text-ink">No automations yet</h3>
            <p className="mt-2 text-sm text-ink-100">
              Set up a recurring campaign that launches on a schedule from your fleet data.
            </p>
            <button
              type="button"
              onClick={() => navigate('/client/mcp/automations/new')}
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun transition hover:bg-sun-400"
            >
              Create an automation
            </button>
          </div>
        )}

        {!loading && !error && automations.length > 0 && (
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {automations.map(a => {
              const status = a.status || (a.enabled ? 'active' : 'paused');
              const nextRun = a.nextRunAt || a.next_run ? new Date(a.nextRunAt || a.next_run).toLocaleString() : '—';
              const cycleModel = a.cycleModel || a.cycle_model || 'rolling_replace';
              return (
                <div
                  key={a.id}
                  className="block rounded-3xl border border-paper-300 bg-white p-5 shadow-card transition hover:border-ink/20 cursor-pointer"
                  onClick={() => navigate(`/client/mcp/automations/${a.id}`)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display text-lg font-bold text-ink">
                      {a.name || 'Untitled automation'}
                    </h3>
                    <span className={`pill ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
                      {STATUS_LABELS[status] || status || 'Draft'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-ink-100">
                    {a.cadence
                      ? cadenceSummary(a)
                      : `${TRIGGER_LABELS[a.trigger_type] || a.trigger_type || 'Recurring'} · ${a.description || 'Launches a campaign on a schedule from your fleet data.'}`
                    }
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-paper p-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.12em] text-ink-200">Next run</div>
                      <div className="mt-0.5 text-sm font-semibold text-ink">{nextRun}</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.12em] text-ink-200">Cycle model</div>
                      <div className="mt-0.5 text-sm font-semibold text-ink">
                        {CYCLE_MODEL_LABELS[cycleModel] || cycleModel}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default McpAutomations;
